/**
 * Utility functions for handling SSE streaming with typing effect
 */

export interface StreamingOptions {
  typingSpeed?: number; // Characters per interval (default: 1)
  typingInterval?: number; // Interval in milliseconds (default: 30)
  onProgress?: (text: string) => void;
  onComplete?: (fullText: string) => void;
  onError?: (error: Error) => void;
}

export interface StreamController {
  stop: () => void;
  isActive: () => boolean;
}

/**
 * Creates a typing effect for streaming text
 */
export const createTypingEffect = (
  fullText: string,
  callback: (currentText: string) => void,
  options: StreamingOptions = {}
): StreamController => {
  const {
    typingSpeed = 1,
    typingInterval = 10,
    onProgress = () => {},
    onComplete = () => {},
  } = options;

  let currentIndex = 0;
  let isActive = true;
  let intervalId: NodeJS.Timeout;

  const typeNextChars = () => {
    if (!isActive || currentIndex >= fullText.length) {
      isActive = false;
      clearInterval(intervalId);
      onComplete(fullText);
      return;
    }

    // Type multiple characters at once based on typingSpeed
    const endIndex = Math.min(currentIndex + typingSpeed, fullText.length);
    const currentText = fullText.substring(0, endIndex);
    
    callback(currentText);
    onProgress(currentText);
    
    currentIndex = endIndex;
  };

  intervalId = setInterval(typeNextChars, typingInterval);

  return {
    stop: () => {
      isActive = false;
      clearInterval(intervalId);
    },
    isActive: () => isActive,
  };
};

/**
 * Creates a streaming SSE connection that displays content immediately as it arrives
 */
export const createStreamingChat = (
  conversationId: string, // Now a UUID string
  query: string,
  onMessage: (text: string, isComplete: boolean) => void,
  options: StreamingOptions = {}
): StreamController => {
  let isActive = true;
  let accumulatedText = '';
  let eventSource: EventSource | null = null;

  const baseUrl = window.location.origin;
  
  const startStream = () => {
    try {
      // Create URL with query parameters for GET request
      const url = new URL(`${baseUrl}/api/app/chat`);
      url.searchParams.append('query', query);
      url.searchParams.append('conversationId', conversationId);

      // Create EventSource connection
      eventSource = new EventSource(url.toString());

      // Handle message events (default event type)
      eventSource.addEventListener('message', (event) => {
        if (!isActive) return;
        
        try {
          const data = event.data?.trim();
          if (data && data !== '[DONE]') {
            let content = '';
            
            if (data.startsWith('{')) {
              const parsed = JSON.parse(data);
              
              // Handle new format: {"message":{"type":"text","text":" I can assist you!\n"},"conversation_id":"50229227326","answer_id":"answer_ffqyadn"}
              if (parsed.message && parsed.message.type === 'text' && parsed.message.text) {
                content = parsed.message.text;
              } else {
                // Fallback to other formats
                content = parsed.content || parsed.text || parsed.message || '';
              }
            } else {
              content = data;
            }
            
            if (content) {
              accumulatedText += content;
              // Show content immediately as it arrives (streaming display)
              onMessage(accumulatedText, false);
            }
          }
        } catch (e) {
          // If not JSON, treat as plain text
          if (event.data && event.data !== '[DONE]') {
            accumulatedText += event.data;
            // Show content immediately as it arrives (streaming display)
            onMessage(accumulatedText, false);
          }
        }
      });

      // Handle stream end - listen for 'message.end' event
      eventSource.addEventListener('message.end', () => {
        // Mark the message as complete (no typing effect needed since content is already shown)
        onMessage(accumulatedText, true);
        options.onComplete?.(accumulatedText);
        cleanup();
      });

      // Handle connection open
      eventSource.addEventListener('open', () => {
        // Connection established
      });

      // Handle errors
      eventSource.addEventListener('error', (error) => {
        if (isActive) {
          options.onError?.(new Error('SSE connection failed'));
        }
        cleanup();
      });

    } catch (error) {
      if (isActive) {
        options.onError?.(error as Error);
      }
    }
  };

  const cleanup = () => {
    if (eventSource) {
      eventSource.close();
      eventSource = null;
    }
    isActive = false;
  };

  // For POST requests, we need to send the data first, then establish SSE connection
  // This is a common pattern where you POST to initiate the conversation, then GET SSE updates
  const initializeAndStream = async () => {
    try {
      // First, send the message via POST
      const response = await fetch(`${baseUrl}/api/app/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query,
          conversationId,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Check if response is SSE stream
      const contentType = response.headers.get('Content-Type');
      
      if (contentType?.includes('text/event-stream')) {
        // Handle SSE stream directly from POST response
        handleStreamFromResponse(response);
      } else {
        // If POST doesn't return SSE, try EventSource approach
        startStream();
      }
    } catch (error) {
      if (isActive) {
        options.onError?.(error as Error);
      }
    }
  };

  const handleStreamFromResponse = async (response: Response) => {
    if (!response.body) {
      throw new Error('No response body');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();

    try {
      while (isActive) {
        const { done, value } = await reader.read();
        
        if (done) {
          // Mark the message as complete when stream ends
          onMessage(accumulatedText, true);
          options.onComplete?.(accumulatedText);
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!isActive) break;
          
          if (line.startsWith('data:')) {
            const data = line.slice(5).trim();
            
            if (data && data !== '[DONE]') {
              try {
                let content = '';
                if (data.startsWith('{')) {
                  const parsed = JSON.parse(data);
                  
                  if (parsed.message && parsed.message.type === 'text' && parsed.message.text) {
                    content = parsed.message.text;
                  } else {
                    content = parsed.content || parsed.text || parsed.message || '';
                  }
                } else {
                  content = data;
                }
                
                if (content) {
                  accumulatedText += content;
                  // Show content immediately as it arrives
                  onMessage(accumulatedText, false);
                }
              } catch (e) {
                accumulatedText += data;
                // Show content immediately as it arrives
                onMessage(accumulatedText, false);
              }
            } else if (data === '[DONE]') {
              // Mark the message as complete when we see [DONE]
              onMessage(accumulatedText, true);
              options.onComplete?.(accumulatedText);
              return; // Exit the function
            }
          } else if (line.startsWith('event:message.end')) {
            // Mark the message as complete when we see event: end
            onMessage(accumulatedText, true);
            options.onComplete?.(accumulatedText);
            return; // Exit the function
          }
        }
      }
    } catch (error) {
      if (isActive) {
        options.onError?.(error as Error);
      }
    }
  };

  // Start the process
  initializeAndStream();

  return {
    stop: () => {
      isActive = false;
      cleanup();
    },
    isActive: () => isActive,
  };
};

/**
 * Utility to clean and format streaming text
 */
export const cleanStreamingText = (text: string): string => {
  return text
    .replace(/\n{3,}/g, '\n\n') // Replace multiple newlines with double newline
    .replace(/^\s+|\s+$/g, '') // Trim whitespace
    .replace(/\s{2,}/g, ' '); // Replace multiple spaces with single space
};

/**
 * Utility to detect if a message is complete based on common patterns
 */
export const isMessageComplete = (text: string): boolean => {
  const trimmed = text.trim();
  if (!trimmed) return false;
  
  // Check for common sentence endings
  const endings = ['.', '!', '?', ':', ')'];
  const lastChar = trimmed[trimmed.length - 1];
  
  return endings.includes(lastChar);
};

/**
 * Utility to estimate reading time for typing speed adjustment
 */
export const calculateOptimalTypingSpeed = (textLength: number): { speed: number; interval: number } => {
  // Adjust typing speed based on text length for better UX
  if (textLength < 50) {
    return { speed: 1, interval: 50 }; // Slow for short messages
  } else if (textLength < 200) {
    return { speed: 2, interval: 30 }; // Medium for medium messages  
  } else {
    return { speed: 3, interval: 20 }; // Fast for long messages
  }
};
